"use client";

import React from "react";
import { useField } from "formik";
import { AlertCircle } from "lucide-react";

interface InputFieldProps {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  min?: string | number;
  max?: string | number;
  minLength?: number;
  maxLength?: number;
  inputClassName?: string;
  labelClassName?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  variant?: "light" | "dark";
}

export const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  type = "text",
  placeholder,
  disabled = false,
  className = "",
  required = false,
  min,
  max,
  minLength,
  maxLength,
  inputClassName = "",
  labelClassName = "",
  onChange: customOnChange,
  onBlur: customOnBlur,
  icon,
  variant = "light",
}) => {
  const [field, meta, helpers] = useField(name);
  const isError = meta.touched && meta.error;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (maxLength !== undefined && value.length > maxLength) {
      value = value.slice(0, maxLength);
    }

    if (type === "number" && max !== undefined && value !== "") {
      if (Number(value) > Number(max)) {
        value = String(max);
      }
    }

    helpers.setValue(value);
    if (customOnChange) customOnChange(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(e);
    if (customOnBlur) customOnBlur(e);
  };

  const isDark = variant === "dark";

  const defaultInputStyles = isError
    ? `border-red-500/50 ${isDark ? "bg-red-500/10" : "bg-red-50/10"} focus:border-red-500 focus:ring-4 focus:ring-red-500/10`
    : isDark
    ? "border-border-1 bg-black-2 text-text-1 focus:border-gold-dim placeholder:text-text-3"
    : "border-border-1 focus:border-gold-core/50 focus:bg-white focus:ring-4 focus:ring-gold-core/10 group-hover:border-gold-dim/30";

  const defaultLabelStyles = isDark
    ? "text-[10px] text-text-3 font-bold tracking-widest uppercase flex items-center gap-2"
    : "text-[10px] uppercase tracking-[0.2em] text-text-3 font-bold";

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <label htmlFor={name} className={labelClassName || defaultLabelStyles}>
          {icon && <span className="text-gold-dim">{icon}</span>}
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      </div>
      <div className="relative group">
        <input
          {...field}
          onChange={handleChange}
          onBlur={handleBlur}
          id={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          min={min}
          max={max}
          minLength={minLength}
          maxLength={maxLength}
          className={`w-full px-4 py-2.5 text-xs outline-none transition-all rounded-lg placeholder:text-slate-400 font-medium tracking-wide disabled:opacity-50 disabled:cursor-not-allowed border
            ${inputClassName || defaultInputStyles}`}
        />
        {isError && (
          <div className="flex items-center gap-1 mt-1 text-[10px] text-red-500 font-bold animate-in fade-in slide-in-from-right-1">
            <AlertCircle size={10} />
            <span>{meta.error}</span>
          </div>
        )}
      </div>
    </div>
  );
};
